/**
 * PKCE (Proof Key for Code Exchange) Service
 * Handles the generation and management of PKCE parameters for OAuth 2.0 flows
 */

import { IS_TAURI } from '../config';

class PKCEService {
  private static readonly CODE_VERIFIER_KEY = 'pkce_code_verifier';
  private static readonly CODE_CHALLENGE_KEY = 'pkce_code_challenge';

  /**
   * Generate a cryptographically random code verifier
   */
  generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return this.base64URLEncode(array);
  }

  /**
   * Generate code challenge from verifier using SHA256
   */
  async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return this.base64URLEncode(new Uint8Array(digest));
  }

  /**
   * Base64URL encoding (RFC 4648 Section 5)
   */
  private base64URLEncode(buffer: Uint8Array): string {
    return btoa(String.fromCharCode.apply(null, Array.from(buffer)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private write(key: string, value: string): void {
    sessionStorage.setItem(key, value);
    if (IS_TAURI) {
      localStorage.setItem(key, value);
    }
  }

  private read(key: string): string | null {
    const fromSession = sessionStorage.getItem(key);
    if (fromSession) return fromSession;
    if (IS_TAURI) return localStorage.getItem(key);
    return null;
  }

  private remove(key: string): void {
    sessionStorage.removeItem(key);
    if (IS_TAURI) {
      localStorage.removeItem(key);
    }
  }

  /**
   * Store PKCE parameters in session storage.
   * The desktop shell also mirrors them in localStorage so the verifier
   * survives the IdP redirect back into the WebView.
   */
  storePKCEParameters(codeVerifier: string, codeChallenge: string): void {
    this.write(PKCEService.CODE_VERIFIER_KEY, codeVerifier);
    this.write(PKCEService.CODE_CHALLENGE_KEY, codeChallenge);
  }

  /**
   * Retrieve code verifier from session storage
   */
  getCodeVerifier(): string | null {
    return this.read(PKCEService.CODE_VERIFIER_KEY);
  }

  /**
   * Retrieve code challenge from session storage
   */
  getCodeChallenge(): string | null {
    return this.read(PKCEService.CODE_CHALLENGE_KEY);
  }

  /**
   * Clear PKCE parameters from session storage (and localStorage in Tauri).
   */
  clearPKCEParameters(): void {
    this.remove(PKCEService.CODE_VERIFIER_KEY);
    this.remove(PKCEService.CODE_CHALLENGE_KEY);
  }

  /**
   * Generate and store PKCE parameters
   */
  async generateAndStorePKCEParameters(): Promise<{ codeVerifier: string; codeChallenge: string }> {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    
    this.storePKCEParameters(codeVerifier, codeChallenge);
    
    return { codeVerifier, codeChallenge };
  }

  /**
   * Check if PKCE is supported by the browser
   */
  isPKCESupported(): boolean {
    return !!(window.crypto && window.crypto.subtle && window.crypto.getRandomValues);
  }
}

// Export a singleton instance
export const pkceService = new PKCEService();
export default pkceService; 